import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { DateTime } from "luxon"

import BarGauge from "@/components/BarGauge"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import type { KPI, KPIRequest } from "@/types/kpiTypes"

import { getIncentiveGauge } from "../api/getIncentiveGauge"

export default function IncentiveBar({
  asOf,
  label,
  kpi,
  fetcher,
}: {
  asOf: Date
  label: string
  kpi: KPIRequest
  fetcher?: () => Promise<KPI>
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["incentiveGauge", { kpi, asOf }],
    queryFn: () =>
      fetcher
        ? fetcher()
        : getIncentiveGauge(DateTime.fromJSDate(asOf).endOf("month").toJSDate(), kpi),
    placeholderData: keepPreviousData,
    refetchInterval: 10 * 60 * 1000,
  })

  if (isPending)
    return (
      <div className="rounded-md shadow-sm border flex flex-col col-span-2">
        <h2 className="text-lg font-semibold bg-accent px-4 pt-3 border-b h-[52.5px] shrink-0">
          {label}
        </h2>
        <div className="w-full h-full p-4">
          <Skeleton className="w-full h-full" />
        </div>
      </div>
    )

  if (isError)
    return (
      <div className="rounded-md shadow-sm border flex flex-col col-span-2">
        <h2 className="text-lg font-semibold bg-accent px-4 pt-3 border-b h-[52.5px]">{label}</h2>
        Network Error
      </div>
    )

  const actuals = data.values?.filter((v) => v.type == "Actual" && new Date(v.date) <= asOf) || []
  const targets = data.values?.filter((v) => v.type == "Target") || []

  const actual = actuals.reduce((a, c) => a + c.value, 0) || 0
  const target = targets.reduce((a, c) => a + c.value, 0) || 0

  return (
    <div className="rounded-md shadow-sm border flex flex-col col-span-2">
      <h2 className="bg-card-header text-lg font-semibold px-4 pt-3 border-b h-[52.5px] shrink-0 rounded-t-md">
        {label}
      </h2>
      <div className="flex flex-col justify-center items-center h-full mt-4 mb-4 grow-0">
        {targets.length == 0 ? (
          <i>Target not yet available</i>
        ) : (
          <BarGauge
            actual={actual || 0}
            budget={target || 0}
            days={actuals.length}
            monthDays={DateTime.fromJSDate(asOf).daysInMonth || 0}
          />
        )}
        <Label className="text-center text-muted-foreground text-[12pt] font-semibold">
          {data.name} <i>({data.unit})</i>
        </Label>
      </div>
    </div>
  )
}
